/**
 *
 * PhoneAuthenticatorLayout
 *
 */

import { stylize } from "@envestnet/envreact-component-library";
import classnames from "classnames";
import React, { useEffect, useState } from "react";
import withIntl from "utils/withIntl";

import { useLocation } from "react-router-dom";

import { PhoneResponsePayload } from "types/PhoneAuthenticator";
import DashboardMfaLayout from "components/DashboardMfaLayout";
import CentricLayout from "components/CentricLayout";
import { mfaFlow } from "utils/enum";
import messages from "./messages";
import styles, { Styles } from "./styles";
import PhoneAuth from "./phoneAuth";

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  // formatMessage: FormatMessage;

  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;
}

interface DefaultProps {
  // Default props should always be required props, i.e. no ? indicating it's optional
  // The default values are specified below
}

export interface PhoneAuthenticatorLayoutProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;

  formatMessage: FormatMessage;

  sendTextMessage: (phoneNumber: string) => void;

  textMessageReceived: PhoneResponsePayload;

  loading: boolean;

  goToMFAScreen: Function;

  updateCurrentFlow: Function;

  submitPassCode: (passcode: string) => void;

  cancelPhoneAuthenticator: () => void;

  verifyPassCodeSuccess: PhoneResponsePayload;

  userName: string;
}

const defaultProps: DefaultProps = {};

type Props = InjectedProps & PhoneAuthenticatorLayoutProps;

const PhoneAuthenticatorLayout = (props: Props) => {
  const {
    classes,
    style,
    className,
    formatMessage,
    innerRef,
    sendTextMessage,
    textMessageReceived,
    loading,
    goToMFAScreen,
    submitPassCode,
    updateCurrentFlow,
    verifyPassCodeSuccess,
    cancelPhoneAuthenticator,
    userName,
  } = props;

  const [passCodeError, setPassCodeError] = useState<string | null>(null);
  const { state } = useLocation();

  const cancelCallback = () => {
    cancelPhoneAuthenticator();
    goToMFAScreen(state.navigationFrom);
  };

  useEffect(() => {
    if (verifyPassCodeSuccess === "SUCCESS") {
      setPassCodeError(null);
      goToMFAScreen(state.navigationFrom);
    } else if (verifyPassCodeSuccess === "INVALID") {
      setPassCodeError(formatMessage(messages.invalidPassCode));
    }
  }, [verifyPassCodeSuccess]);

  useEffect(() => {
    if (state?.currentLayout) {
      updateCurrentFlow(state?.currentLayout);
    }
  }, [state?.currentLayout]);

  return state?.currentLayout === mfaFlow.MFA ? (
    // @ts-ignore
    <DashboardMfaLayout>
      <CentricLayout className={classes.mfaCenter}>
        <PhoneAuth
          hideLogo
          userName={userName}
          textMessageReceived={textMessageReceived}
          loading={loading}
          cancelCallback={cancelCallback}
          submitPassCode={submitPassCode}
          passCodeError={passCodeError}
          setPassCodeError={setPassCodeError}
          sendTextMessage={sendTextMessage}
          restartPhone={cancelPhoneAuthenticator}
        />
      </CentricLayout>
    </DashboardMfaLayout>
  ) : (
    <div
      ref={innerRef}
      className={classnames(className, classes.container)}
      style={style}
    >
      <CentricLayout className={classes.mfaCenter}>
        <PhoneAuth
          hideLogo={false}
          userName={userName}
          textMessageReceived={textMessageReceived}
          loading={loading}
          cancelCallback={cancelCallback}
          submitPassCode={submitPassCode}
          passCodeError={passCodeError}
          setPassCodeError={setPassCodeError}
          sendTextMessage={sendTextMessage}
          restartPhone={cancelPhoneAuthenticator}
        />
      </CentricLayout>
    </div>
  );
};

PhoneAuthenticatorLayout.defaultProps = defaultProps;

export default withIntl(
  stylize<Props, DefaultProps>(styles)(PhoneAuthenticatorLayout)
);
